<template>
  <div class="container">
    <h1 class="fw-bold text-center mb-4">IAF CertSearch 동기화 관리</h1>

    <!-- 전체 로딩 오버레이 -->
    <div v-if="globalLoading" class="loading-overlay">
      <div class="loading-content">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <div class="mt-3">
          <h5>{{ loadingMessage }}</h5>
          <p class="text-muted">{{ loadingSubMessage }}</p>
          <div v-if="progress.show" class="progress mt-2" style="height: 20px">
            <div
              class="progress-bar"
              role="progressbar"
              :style="`width: ${progress.percentage}%`"
              :aria-valuenow="progress.percentage"
              aria-valuemin="0"
              aria-valuemax="100"
            >
              {{ progress.current }} / {{ progress.total }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 탭 -->
    <ul class="nav nav-tabs mb-4">
      <li class="nav-item">
        <a
          :class="['nav-link', tab === 'missing' && 'active']"
          @click="switchTab('missing')"
          :disabled="globalLoading"
          >누락 인증서 업로드</a
        >
      </li>
      <li class="nav-item">
        <a
          :class="['nav-link', tab === 'status' && 'active']"
          @click="switchTab('status')"
          :disabled="globalLoading"
          >상태 동기화</a
        >
      </li>
    </ul>

    <!-- 동기화 현황 카드 (공통) -->
    <div class="card mb-3" v-if="syncStatus">
      <div class="card-body">
        <div class="row text-center">
          <div class="col">
            <h4 class="text-primary">{{ syncStatus.internal_count || 0 }}</h4>
            <small>KAICMS 인증서</small>
            <br /><small class="text-muted">(취소 제외)</small>
          </div>
          <div class="col">
            <h4 class="text-success">{{ syncStatus.iaf_count || 0 }}</h4>
            <small>IAF KAICMS 인증서</small>
            <br /><small class="text-muted">(동기화 대상)</small>
          </div>
          <div class="col">
            <h4 class="text-info">{{ syncStatus.total_iaf_count || 0 }}</h4>
            <small>IAF 전체 인증서</small>
            <br /><small class="text-muted">(모든 CB 포함)</small>
          </div>
          <div class="col">
            <h4
              :class="
                syncStatus.difference > 0
                  ? 'text-warning'
                  : syncStatus.difference < 0
                  ? 'text-danger'
                  : 'text-success'
              "
            >
              {{ syncStatus.difference || 0 }}
            </h4>
            <small>동기화 차이</small>
            <br /><small class="text-muted">(KAICMS - IAF)</small>
          </div>
          <div class="col">
            <button
              class="btn btn-outline-secondary btn-sm"
              @click="refreshAll"
              :disabled="globalLoading"
            >
              <i
                class="fas fa-sync-alt"
                :class="{ 'fa-spin': localLoading.refresh }"
              ></i>
              새로고침
            </button>
            <br /><small class="text-muted mt-1">수동 새로고침</small>
          </div>
        </div>

        <!-- 마지막 확인 시간 표시 -->
        <div class="text-center mt-2" v-if="syncStatus.last_checked">
          <small class="text-muted">
            <i class="fas fa-clock me-1"></i>
            마지막 확인: {{ formatDateTime(syncStatus.last_checked) }}
          </small>
        </div>

        <!-- ✅ 임시 디버깅 정보 추가 -->
        <div class="text-center mt-2" style="font-size: 10px; color: #999">
          <details>
            <summary>디버깅 정보</summary>
            <pre>{{ JSON.stringify(syncStatus, null, 2) }}</pre>
          </details>
        </div>
      </div>
    </div>

    <!-- 검색 박스 -->
    <div class="mb-3 d-flex">
      <input
        v-model="searchQuery"
        class="form-control"
        placeholder="인증번호 / 회사명 검색…"
        :disabled="globalLoading"
      />
      <button
        class="btn btn-outline-secondary ms-2"
        @click="searchQuery = ''"
        :disabled="globalLoading"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- ---------- ① 누락 인증서 ---------- -->
    <div v-if="tab === 'missing'">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h5 class="mb-0">
          누락 인증서
          <span class="badge bg-warning">{{ filteredMissing.length }}</span>
        </h5>
        <button
          class="btn btn-info btn-sm"
          @click="getMissingCerts"
          :disabled="globalLoading"
        >
          <i
            class="fas fa-sync-alt"
            :class="{ 'fa-spin': localLoading.missing }"
          ></i>
          새로고침
        </button>
      </div>

      <div class="table-responsive">
        <table class="table table-striped align-middle">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  class="form-check-input"
                  :checked="isAllSelected('missing')"
                  @change="toggleSelectAll('missing')"
                  :disabled="globalLoading"
                />
              </th>
              <th>인증번호</th>
              <th>회사명</th>
              <th>상태</th>
              <th>최초 발행일</th>
              <th>만료일</th>
              <th>액션</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="cert in filteredMissing" :key="cert.cert_no">
              <td>
                <input
                  type="checkbox"
                  class="form-check-input"
                  :value="cert.cert_no"
                  v-model="selectedMissing"
                  :disabled="globalLoading"
                />
              </td>
              <td>{{ cert.cert_no }}</td>
              <td>{{ cert.name_ko || cert.name_en || 'N/A' }}</td>
              <td>
                <span class="badge" :class="badgeClass(cert.status)">
                  {{ statusText(cert.status) }}</span
                >
              </td>
              <td>{{ fmtDate(cert.cert_init_date) }}</td>
              <td>{{ fmtDate(cert.cert_due_date) }}</td>
              <td>
                <button
                  class="btn btn-sm btn-outline-primary"
                  :disabled="globalLoading || localLoading.upload[cert.cert_no]"
                  @click="uploadSingle(cert.cert_no)"
                >
                  <i
                    v-if="localLoading.upload[cert.cert_no]"
                    class="fas fa-spinner fa-spin me-1"
                  ></i>
                  <i v-else class="fas fa-upload me-1"></i>
                  업로드
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="selectedMissing.length" class="text-center my-2">
        <button
          class="btn btn-primary"
          :disabled="globalLoading"
          @click="uploadBulk"
        >
          <i class="fas fa-cloud-upload-alt me-2"></i>
          선택 {{ selectedMissing.length }}개 업로드
        </button>
      </div>
    </div>

    <!-- ---------- ② 상태 동기화 ---------- -->
    <div v-else>
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h5 class="mb-0">
          상태 불일치 인증서
          <span class="badge bg-warning">{{ filteredMismatch.length }}</span>
        </h5>
        <button
          class="btn btn-info btn-sm"
          @click="getMismatches"
          :disabled="globalLoading"
        >
          <i
            class="fas fa-sync-alt"
            :class="{ 'fa-spin': localLoading.mismatch }"
          ></i>
          새로고침
        </button>
      </div>

      <div class="table-responsive">
        <table class="table table-striped align-middle">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  class="form-check-input"
                  :checked="isAllSelected('status')"
                  @change="toggleSelectAll('status')"
                  :disabled="globalLoading"
                />
              </th>
              <th>인증번호</th>
              <th>회사명</th>
              <th>KAICMS 상태</th>
              <th>IAF 상태</th>
              <th>액션</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in filteredMismatch" :key="row.cert_no">
              <td>
                <input
                  type="checkbox"
                  class="form-check-input"
                  :value="row.cert_no"
                  v-model="selectedMismatch"
                  :disabled="globalLoading"
                />
              </td>
              <td>{{ row.cert_no }}</td>
              <td>{{ row.name_ko || row.name_en || 'N/A' }}</td>
              <td>
                <span class="badge" :class="badgeClass(row.kaicms_status)">
                  {{ statusText(row.kaicms_status) }}</span
                >
              </td>
              <td>
                <span class="badge bg-secondary"> {{ row.iaf_status }}</span>
              </td>
              <td>
                <button
                  class="btn btn-sm btn-outline-warning"
                  :disabled="globalLoading || localLoading.sync[row.cert_no]"
                  @click="syncSingle(row.cert_no)"
                >
                  <i
                    v-if="localLoading.sync[row.cert_no]"
                    class="fas fa-spinner fa-spin me-1"
                  ></i>
                  <i v-else class="fas fa-sync-alt me-1"></i>
                  동기화
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="selectedMismatch.length" class="text-center my-2">
        <button
          class="btn btn-warning"
          :disabled="globalLoading"
          @click="syncBulk"
        >
          <i class="fas fa-sync-alt me-2"></i>
          선택 {{ selectedMismatch.length }}개 동기화
        </button>
      </div>
    </div>

    <!-- 성공/실패 토스트 메시지 -->
    <div class="toast-container position-fixed bottom-0 end-0 p-3">
      <div
        v-for="toast in toasts"
        :key="toast.id"
        :class="`toast show align-items-center text-white bg-${toast.type} border-0`"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        <div class="d-flex">
          <div class="toast-body">
            <i class="fas" :class="getToastIcon(toast.type)"></i>
            {{ toast.text }}
          </div>
          <button
            type="button"
            class="btn-close btn-close-white me-2 m-auto"
            @click="removeToast(toast.id)"
          ></button>
        </div>
      </div>
    </div>

    <!-- 결과 요약 모달 -->
    <div v-if="resultSummary" class="modal show d-block" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas" :class="resultSummary.icon"></i>
              {{ resultSummary.title }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="resultSummary = null"
            ></button>
          </div>
          <div class="modal-body">
            <p>{{ resultSummary.message }}</p>
            <div v-if="resultSummary.details && resultSummary.details.length">
              <h6>상세 결과:</h6>
              <ul class="list-group">
                <li
                  v-for="detail in resultSummary.details"
                  :key="detail.cert_no"
                  :class="`list-group-item d-flex justify-content-between align-items-center ${
                    detail.success
                      ? 'list-group-item-success'
                      : 'list-group-item-danger'
                  }`"
                >
                  {{ detail.cert_no }}
                  <span
                    :class="`badge ${
                      detail.success ? 'bg-success' : 'bg-danger'
                    }`"
                  >
                    {{ detail.success ? '성공' : '실패' }}
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="resultSummary = null"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="resultSummary" class="modal-backdrop show"></div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      tab: 'missing',
      syncStatus: null,

      missingCerts: [],
      mismatchCerts: [],

      selectedMissing: [],
      selectedMismatch: [],

      searchQuery: '',

      // 로딩 상태 관리
      globalLoading: false,
      localLoading: {
        refresh: false,
        missing: false,
        mismatch: false,
        upload: {},
        sync: {}
      },

      // 로딩 메시지
      loadingMessage: '',
      loadingSubMessage: '',

      // 진행률 표시
      progress: {
        show: false,
        current: 0,
        total: 0,
        percentage: 0
      },

      // 토스트 메시지 관리
      toasts: [],
      toastIdCounter: 0,

      // 결과 요약 모달
      resultSummary: null
    }
  },
  computed: {
    filteredMissing() {
      if (!this.searchQuery.trim()) return this.missingCerts
      const q = this.searchQuery.toLowerCase()
      return this.missingCerts.filter(
        (c) =>
          (c.cert_no || '').toLowerCase().includes(q) ||
          (c.name_ko || '').toLowerCase().includes(q) ||
          (c.name_en || '').toLowerCase().includes(q)
      )
    },
    filteredMismatch() {
      if (!this.searchQuery.trim()) return this.mismatchCerts
      const q = this.searchQuery.toLowerCase()
      return this.mismatchCerts.filter(
        (c) =>
          (c.cert_no || '').toLowerCase().includes(q) ||
          (c.name_ko || '').toLowerCase().includes(q) ||
          (c.name_en || '').toLowerCase().includes(q)
      )
    }
  },
  async mounted() {
    // 동기화 상태만 자동 조회 (상세 데이터는 수동)
    try {
      await this.getSyncStatus()
      this.showToast(
        '새로고침 버튼을 클릭하여 상세 데이터를 확인하세요.',
        'info'
      )
    } catch (error) {
      console.error('초기 동기화 상태 조회 실패:', error)
      this.showToast(
        '새로고침 버튼을 클릭하여 동기화 상태를 확인하세요.',
        'info'
      )
    }
  },
  methods: {
    /* ---------------- 공통 ---------------- */
    async refreshAll() {
      this.setGlobalLoading(
        true,
        '동기화 현황을 확인하는 중...',
        'IAF CertSearch와 연결 중입니다.'
      )
      this.localLoading.refresh = true

      try {
        console.log('🚀 refreshAll 시작 - getSyncStatus 호출')
        await this.getSyncStatus()
        console.log('✅ getSyncStatus 완료')

        console.log(
          '🚀 다음 단계 시작:',
          this.tab === 'missing' ? 'getMissingCerts' : 'getMismatches'
        )
        await (this.tab === 'missing'
          ? this.getMissingCerts()
          : this.getMismatches())
        console.log('✅ 데이터 조회 완료')

        this.showToast('동기화 현황을 성공적으로 새로고침했습니다.', 'success')
      } catch (error) {
        console.error('❌ refreshAll 에러:', error)
        this.showToast('새로고침 중 오류가 발생했습니다.', 'danger')
      } finally {
        this.setGlobalLoading(false)
        this.localLoading.refresh = false
      }
    },

    async switchTab(newTab) {
      if (this.globalLoading) return

      this.tab = newTab
      this.selectedMissing = []
      this.selectedMismatch = []

      if (newTab === 'missing') {
        await this.getMissingCerts()
      } else {
        await this.getMismatches()
      }
    },

    async getSyncStatus() {
      try {
        const r = await this.$get('/api/iaf-cert-search/sync/status')

        if (r?.success) {
          this.syncStatus = r.data

          // ✅ 서버 상태에 따른 사용자 안내
          if (r.data.server_status === 'unavailable') {
            this.showToast(
              'IAF CertSearch 서버가 일시적으로 사용할 수 없습니다. 잠시 후 다시 시도해주세요.',
              'warning',
              10000
            )
          } else if (r.data.server_status === 'down') {
            this.showToast(
              'IAF CertSearch 서버 연결에 실패했습니다.',
              'danger',
              10000
            )
          }
        }
      } catch (error) {
        console.error('동기화 상태 조회 실패:', error)
        throw error
      }
    },

    /* ---------------- 로딩 상태 관리 ---------------- */
    setGlobalLoading(loading, message = '', subMessage = '') {
      this.globalLoading = loading
      this.loadingMessage = message
      this.loadingSubMessage = subMessage

      if (!loading) {
        this.progress.show = false
        this.progress.current = 0
        this.progress.total = 0
        this.progress.percentage = 0
      }
    },

    setProgress(current, total) {
      this.progress.show = true
      this.progress.current = current
      this.progress.total = total
      this.progress.percentage =
        total > 0 ? Math.round((current / total) * 100) : 0
    },

    /* ---------------- 토스트 메시지 ---------------- */
    showToast(text, type = 'info', duration = 5000) {
      const toast = {
        id: ++this.toastIdCounter,
        text,
        type,
        createdAt: Date.now()
      }

      this.toasts.push(toast)

      setTimeout(() => {
        this.removeToast(toast.id)
      }, duration)
    },

    removeToast(id) {
      const index = this.toasts.findIndex((t) => t.id === id)
      if (index > -1) {
        this.toasts.splice(index, 1)
      }
    },

    getToastIcon(type) {
      const icons = {
        success: 'fa-check-circle',
        danger: 'fa-exclamation-triangle',
        warning: 'fa-exclamation-circle',
        info: 'fa-info-circle'
      }
      return icons[type] || 'fa-info-circle'
    },

    /* ---------------- 결과 요약 모달 ---------------- */
    showResultSummary(title, message, details = null, type = 'info') {
      const icons = {
        success: 'fa-check-circle text-success',
        danger: 'fa-exclamation-triangle text-danger',
        warning: 'fa-exclamation-circle text-warning',
        info: 'fa-info-circle text-info'
      }

      this.resultSummary = {
        title,
        message,
        details,
        icon: icons[type] || icons.info
      }
    },

    /* ---------------- 기존 메소들 ---------------- */
    badgeClass(status) {
      const m = {
        normal: 'bg-success',
        auditRequired: 'bg-primary',
        stop: 'bg-warning',
        canceled: 'bg-danger',
        check: 'bg-info'
      }
      return m[status] || 'bg-secondary'
    },
    statusText(st) {
      const m = {
        normal: '정상',
        auditRequired: '심사필요',
        stop: '정지',
        canceled: '취소',
        check: '확인필요'
      }
      return m[st] || st
    },
    fmtDate(d) {
      if (!d) return 'N/A'
      return d.includes('-')
        ? d
        : d.length === 8
        ? `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6)}`
        : d
    },
    isAllSelected(kind) {
      return kind === 'missing'
        ? this.selectedMissing.length === this.filteredMissing.length &&
            this.filteredMissing.length > 0
        : this.selectedMismatch.length === this.filteredMismatch.length &&
            this.filteredMismatch.length > 0
    },
    toggleSelectAll(kind) {
      if (kind === 'missing') {
        this.selectedMissing = this.isAllSelected('missing')
          ? []
          : this.filteredMissing.map((c) => c.cert_no)
      } else {
        this.selectedMismatch = this.isAllSelected('status')
          ? []
          : this.filteredMismatch.map((c) => c.cert_no)
      }
    },

    /* --------- 누락 인증서 --------- */
    async getMissingCerts() {
      this.setGlobalLoading(
        true,
        '누락된 인증서를 조회하는 중...',
        'KAICMS와 IAF CertSearch 데이터를 비교하고 있습니다.'
      )
      this.localLoading.missing = true

      try {
        const r = await this.$get('/api/iaf-cert-search/missing')

        // ✅ 디버깅: getMissingCerts 응답 분석
        console.log('🔍 === getMissingCerts 응답 분석 시작 ===')
        console.log('🔍 전체 응답:', r)
        console.log('🔍 r.success:', r.success)
        console.log('🔍 r.data:', r.data)
        console.log('🔍 r.data.success:', r.data?.success)
        console.log('🔍 r.missing_certifications:', r.missing_certifications)
        console.log(
          '🔍 r.data.missing_certifications:',
          r.data?.missing_certifications
        )
        console.log('🔍 === getMissingCerts 응답 분석 종료 ===')

        if (r?.success) {
          this.missingCerts = (r.missing_certifications || []).filter(
            (c) => c.cert_init_date && c.cert_init_date.trim() !== ''
          )

          this.showToast(
            `누락된 인증서 ${this.missingCerts.length}개를 조회했습니다.`,
            'info'
          )
        } else {
          console.error('❌ getMissingCerts 실패 - 응답:', r)
          throw new Error(r?.message || '누락 인증서 조회 실패')
        }
      } catch (error) {
        this.missingCerts = []
        this.showToast('누락 인증서 조회 중 오류가 발생했습니다.', 'danger')
        console.error('누락 인증서 조회 실패:', error)
      } finally {
        this.setGlobalLoading(false)
        this.localLoading.missing = false
      }
    },

    async uploadSingle(certNo) {
      if (!confirm(`${certNo}를 IAF CertSearch에 업로드하시겠습니까?`)) return

      this.localLoading.upload[certNo] = true
      this.$forceUpdate() // Vue 2 reactivity 문제 해결

      try {
        this.showToast(`${certNo} 업로드를 시작합니다...`, 'info')

        const r = await this.$post('/api/iaf-cert-search/upload/single', {
          cert_no: certNo
        })

        // ✅ 클라이언트 응답 상세 분석
        console.log('🔍 === 클라이언트 응답 분석 시작 ===')
        console.log('🔍 전체 응답 객체:', r)
        console.log('🔍 r.success:', r.data.success)
        console.log('🔍 r.success 타입:', typeof r.data.success)
        console.log('🔍 r.success === true:', r.data.success === true)
        console.log('🔍 r.message:', r.data.message)
        console.log('🔍 r.data:', r.data)
        console.log('🔍 HTTP 상태 정보:', r.status || 'status 없음')
        console.log('🔍 === 클라이언트 응답 분석 종료 ===')

        if (r?.data?.success) {
          this.showToast(`${certNo} 업로드가 성공했습니다.`, 'success')
          this.missingCerts = this.missingCerts.filter(
            (c) => c.cert_no !== certNo
          )

          // 동기화 상태 업데이트
          await this.getSyncStatus()
        } else {
          throw new Error(r?.data?.message || '업로드 실패')
        }
      } catch (error) {
        this.showToast(`${certNo} 업로드 실패: ${error.message}`, 'danger')
        console.error('단일 업로드 실패:', error)
      } finally {
        this.localLoading.upload[certNo] = false
        this.$forceUpdate()
      }
    },

    async uploadBulk() {
      const count = this.selectedMissing.length
      if (
        !confirm(
          `선택한 ${count}개 인증서를 IAF CertSearch에 업로드하시겠습니까?`
        )
      ) {
        return
      }

      this.setGlobalLoading(
        true,
        `${count}개 인증서를 업로드하는 중...`,
        '각 인증서를 순차적으로 처리하고 있습니다.'
      )

      try {
        this.setProgress(0, count)

        const r = await this.$post('/api/iaf-cert-search/upload/bulk', {
          cert_nos: this.selectedMissing
        })

        if (r?.data?.success) {
          const { success = 0, failed = 0, details = [] } = r.summary || {}

          this.showResultSummary(
            '일괄 업로드 완료',
            `총 ${count}개 중 ${success}개 성공, ${failed}개 실패`,
            details,
            failed > 0 ? 'warning' : 'success'
          )

          this.showToast(
            `일괄 업로드 완료: 성공 ${success}개, 실패 ${failed}개`,
            failed > 0 ? 'warning' : 'success'
          )

          // 성공한 항목들 제거
          const successCertNos = details
            .filter((d) => d.success)
            .map((d) => d.cert_no)
          this.missingCerts = this.missingCerts.filter(
            (c) => !successCertNos.includes(c.cert_no)
          )
          this.selectedMissing = []

          // 동기화 상태 업데이트
          await this.getSyncStatus()
        } else {
          throw new Error(r?.data?.message || '일괄 업로드 실패')
        }
      } catch (error) {
        this.showToast(`일괄 업로드 실패: ${error.message}`, 'danger')
        console.error('일괄 업로드 실패:', error)
      } finally {
        this.setGlobalLoading(false)
      }
    },

    /* --------- 상태 불일치 --------- */
    async getMismatches() {
      this.setGlobalLoading(
        true,
        '상태 불일치 인증서를 조회하는 중...',
        'KAICMS와 IAF CertSearch의 상태를 비교하고 있습니다.'
      )
      this.localLoading.mismatch = true

      try {
        const r = await this.$get('/api/iaf-cert-search/status-mismatch')

        // ✅ 디버깅: getMismatches 응답 분석
        console.log('🔍 === getMismatches 응답 분석 시작 ===')
        console.log('🔍 전체 응답:', r)
        console.log('🔍 r.success:', r.success)
        console.log('🔍 r.data:', r.data)
        console.log('🔍 r.data.success:', r.data?.success)
        console.log(
          '🔍 r.mismatched_certifications:',
          r.mismatched_certifications
        )
        console.log(
          '🔍 r.data.mismatched_certifications:',
          r.data?.mismatched_certifications
        )
        console.log('🔍 === getMismatches 응답 분석 종료 ===')

        if (r?.success) {
          this.mismatchCerts = r.mismatched_certifications || []
          this.showToast(
            `상태 불일치 인증서 ${this.mismatchCerts.length}개를 조회했습니다.`,
            'info'
          )
        } else {
          console.error('❌ getMismatches 실패 - 응답:', r.data || r)
          throw new Error(r?.data?.message || '상태 불일치 조회 실패')
        }
      } catch (error) {
        this.mismatchCerts = []
        this.showToast('상태 불일치 조회 중 오류가 발생했습니다.', 'danger')
        console.error('상태 불일치 조회 실패:', error)
      } finally {
        this.setGlobalLoading(false)
        this.localLoading.mismatch = false
      }
    },

    async syncSingle(certNo) {
      if (!confirm(`${certNo}의 상태를 동기화하시겠습니까?`)) return

      this.localLoading.sync[certNo] = true
      this.$forceUpdate()

      try {
        this.showToast(`${certNo} 상태 동기화를 시작합니다...`, 'info')

        // 해당 인증서의 현재 KAICMS 상태 찾기
        const mismatchCert = this.mismatchCerts.find(
          (c) => c.cert_no === certNo
        )
        if (!mismatchCert) {
          throw new Error('해당 인증서 정보를 찾을 수 없습니다.')
        }

        console.log(`🔍 ${certNo} 상태 동기화:`, {
          kaicms_status: mismatchCert.kaicms_status,
          iaf_status: mismatchCert.iaf_status
        })

        const r = await this.$put('/api/iaf-cert-search/update/status', {
          cert_no: certNo,
          status: mismatchCert.kaicms_status // ✅ KAICMS 상태 추가
        })

        if (r?.data?.success) {
          this.showToast(`${certNo} 상태 동기화가 성공했습니다.`, 'success')
          this.mismatchCerts = this.mismatchCerts.filter(
            (c) => c.cert_no !== certNo
          )

          // 동기화 상태 업데이트
          await this.getSyncStatus()
        } else {
          throw new Error(r?.data?.message || '동기화 실패')
        }
      } catch (error) {
        this.showToast(`${certNo} 상태 동기화 실패: ${error.message}`, 'danger')
        console.error('단일 동기화 실패:', error)
      } finally {
        this.localLoading.sync[certNo] = false
        this.$forceUpdate()
      }
    },

    async syncBulk() {
      const count = this.selectedMismatch.length
      if (!confirm(`선택한 ${count}개 인증서의 상태를 동기화하시겠습니까?`)) {
        return
      }

      this.setGlobalLoading(
        true,
        `${count}개 인증서 상태를 동기화하는 중...`,
        '각 인증서의 상태를 순차적으로 업데이트하고 있습니다.'
      )

      try {
        this.setProgress(0, count)

        const r = await this.$put('/api/iaf-cert-search/update/bulk-status', {
          cert_nos: this.selectedMismatch
        })

        if (r?.data?.success) {
          const { success = 0, failed = 0, details = [] } = r.summary || {}

          this.showResultSummary(
            '일괄 상태 동기화 완료',
            `총 ${count}개 중 ${success}개 성공, ${failed}개 실패`,
            details,
            failed > 0 ? 'warning' : 'success'
          )

          this.showToast(
            `일괄 동기화 완료: 성공 ${success}개, 실패 ${failed}개`,
            failed > 0 ? 'warning' : 'success'
          )

          // 성공한 항목들 제거
          const successCertNos = details
            .filter((d) => d.success)
            .map((d) => d.cert_no)
          this.mismatchCerts = this.mismatchCerts.filter(
            (c) => !successCertNos.includes(c.cert_no)
          )
          this.selectedMismatch = []

          // 동기화 상태 업데이트
          await this.getSyncStatus()
        } else {
          throw new Error(r?.data?.message || '일괄 동기화 실패')
        }
      } catch (error) {
        this.showToast(`일괄 동기화 실패: ${error.message}`, 'danger')
        console.error('일괄 동기화 실패:', error)
      } finally {
        this.setGlobalLoading(false)
      }
    },

    formatDateTime(dateString) {
      if (!dateString) return 'N/A'
      try {
        const date = new Date(dateString)
        const now = new Date()
        const diffMs = now - date
        const diffMins = Math.floor(diffMs / (1000 * 60))

        if (diffMins < 1) return '방금 전'
        if (diffMins < 60) return `${diffMins}분 전`
        if (diffMins < 1440) return `${Math.floor(diffMins / 60)}시간 전`

        return date.toLocaleString('ko-KR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        return dateString
      }
    }
  }
}
</script>

<style scoped>
.table td,
.table th {
  vertical-align: middle;
}

/* 전체 로딩 오버레이 */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-content {
  background: white;
  padding: 2rem;
  border-radius: 0.5rem;
  text-align: center;
  min-width: 300px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* 탭 비활성화 스타일 */
.nav-link:disabled {
  color: #6c757d;
  cursor: not-allowed;
  background-color: #e9ecef;
}

/* 토스트 컨테이너 */
.toast-container {
  z-index: 1050;
}

.toast {
  margin-bottom: 0.5rem;
}

/* 모달 백드롭 */
.modal-backdrop.show {
  opacity: 0.5;
}

/* 진행률 바 */
.progress {
  background-color: #e9ecef;
}

.progress-bar {
  transition: width 0.3s ease;
}

/* 버튼 로딩 애니메이션 */
.fa-spin {
  animation: fa-spin 1s infinite linear;
}

@keyframes fa-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
